import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ToastType =
  | 'transit'
  | 'delivered'
  | 'hold'
  | 'out_for_delivery'
  | 'picked_up'
  | 'success'
  | 'info'
  | 'warning'
  | 'error';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  trackingId?: string; // optional — not every toast is shipment-related
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  addToast: () => undefined,
  removeToast: () => undefined,
});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      setToasts((prev) => [{ ...toast, id }, ...prev].slice(0, 5));
      setTimeout(() => removeToast(id), 6000);
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
